import React, { FormEvent, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Toast, ToastContainer } from "react-bootstrap";
import { getApiErrorMessage } from "../../../store/api/baseApi";
import { useCreateRequestMutation } from "../../../store/api/requestsApi";
import "./HeroSection.css";
import { websitePaths } from "../../../routes/websitePaths";

const slides = [
  {
    title: "Built For Strength, Quality, And Trust",
    description:
      "Construction solutions for homes, commercial spaces, and site development with clear planning and dependable execution.",
    cta: "Request A Quote",
    href: websitePaths.contact,
    image: "/images/IMG-20251110-WA0006.jpg.jpeg",
    highlights: [
      {
        title: "Residential Construction",
        detail:
          "Custom homes and apartments delivered with practical planning and durable execution.",
      },
      {
        title: "Commercial Projects",
        detail:
          "Office and retail construction tailored for function, scale, and long-term value.",
      },
      {
        title: "Site Execution",
        detail:
          "Disciplined on-site coordination to keep work moving safely and on schedule.",
      },
    ],
  },
  {
    title: "Construction Work Planned Around Real Needs",
    description:
      "From upgrades to full-scale builds, we focus on practical design, careful supervision, and durable results.",
    cta: "Explore Services",
    href: `${websitePaths.home}#services`,
    image: "/images/20231022_152856.jpg.jpeg",
    highlights: [
      {
        title: "Renovation And Remodeling",
        detail:
          "Interior and structural improvements that upgrade function, comfort, and overall finish quality.",
      },
      {
        title: "Project Management",
        detail:
          "Practical coordination, milestone tracking, and site follow-up to keep work moving smoothly.",
      },
      {
        title: "Maintenance Support",
        detail:
          "Repair and upkeep services that help properties stay safe, presentable, and ready for use.",
      },
    ],
  },
  {
    title: "A Construction Partner You Can Rely On",
    description:
      "We manage projects from planning to handover with visible progress, disciplined execution, and straightforward communication.",
    cta: "Contact Us",
    href: websitePaths.contact,
    image: "/images/construction3.jpg",
    highlights: [
      {
        title: "Project Management",
        detail:
          "Clear supervision with milestone tracking, vendor coordination, and quality checks.",
      },
      {
        title: "Site Planning",
        detail:
          "Requirement review, work sequencing, and execution planning aligned with scope and timeline.",
      },
      {
        title: "Clear Delivery Process",
        detail:
          "Straightforward updates and structured handover from project start to completion.",
      },
    ],
  },
];

const HeroSection = () => {
  const [createRequest, { isLoading }] = useCreateRequestMutation();
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [validated, setValidated] = useState(false);
  const [requestForm, setRequestForm] = useState({
    name: "",
    phone: "",
    message: "",
  });

  const handleSubmitRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!event.currentTarget.checkValidity()) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    setValidated(true);
    try {
      await createRequest({
        name: requestForm.name,
        phone: requestForm.phone,
        message: requestForm.message,
      }).unwrap();
      setErrorMessage("");
      setShowSuccessToast(true);
      setRequestForm({
        name: "",
        phone: "",
        message: "",
      });
      setValidated(false);
    } catch (apiError) {
      setErrorMessage(getApiErrorMessage(apiError as FetchBaseQueryError));
      setShowErrorToast(true);
    }
  };

  return (
    <section className='hero-wrapper'>
      <div className='hero-top-contact'>
        <div className='container d-flex flex-wrap align-items-center justify-content-between gap-2'>
          <p className='mb-0 fw-semibold'>Speak with our construction team</p>
          <p className='mb-0'>
            Quick help for planning, site discussions, project updates, and
            service requests.
          </p>
        </div>
      </div>

      <section
        id='heroCarousel'
        className='carousel slide carousel-fade hero-carousel'
        data-bs-ride='carousel'
      >
        <div className='carousel-indicators'>
          {slides.map((_, idx) => (
            <button
              key={idx}
              type='button'
              data-bs-target='#heroCarousel'
              data-bs-slide-to={idx}
              className={idx === 0 ? "active" : ""}
              aria-current={idx === 0 ? "true" : undefined}
              aria-label={`Slide ${idx + 1}`}
            ></button>
          ))}
        </div>

        <div className='carousel-inner'>
          {slides.map((slide, idx) => (
            <div
              key={slide.title}
              className={`carousel-item ${idx === 0 ? "active" : ""}`}
            >
              <div
                className='hero-slide d-flex align-items-center text-white'
                style={{ backgroundImage: `url('${slide.image}')` }}
              >
                <div className='overlay'></div>
                <div className='position-relative hero-content container'>
                  <div className='row align-items-center g-4 g-lg-5 hero-grid'>
                    <div className='col-lg-7'>
                      <p className='hero-kicker mb-2'>Construction Services</p>
                      <h1 className='fw-bold'>
                        {slide.title}
                      </h1>
                      <p className='lead mb-3 hero-description'>
                        {slide.description}
                      </p>
                      <p className='hero-support-text mb-3'>
                        Clear guidance for site planning, construction
                        execution, and project coordination.
                      </p>
                      <div className='hero-point-list mb-4'>
                        {slide.highlights.map((point) => (
                          <div key={point.title} className='hero-point-item'>
                            <h3>{point.title}</h3>
                            <p>{point.detail}</p>
                          </div>
                        ))}
                      </div>
                      <div className='d-flex flex-wrap gap-2 hero-actions'>
                        <a
                          href={slide.href}
                          className='btn brand-btn-primary btn-lg px-4'
                        >
                          {slide.cta}
                        </a>
                        <a
                          href={websitePaths.contact}
                          className='btn btn-outline-light btn-lg px-4'
                        >
                          Talk To Our Team
                        </a>
                      </div>
                    </div>
                    <div className='col-lg-5'>
                      <div className='hero-request-box'>
                        <h3 className='h5 mb-2'>Request A Call Back</h3>
                        <p className='mb-3'>
                          Share your requirement and our team will get in touch
                          shortly.
                        </p>
                        <form
                          noValidate
                          className={`hero-request-form ${validated ? "was-validated" : ""}`}
                          onSubmit={handleSubmitRequest}
                        >
                          <input
                            type='text'
                            className='form-control'
                            placeholder='Full Name'
                            aria-label='Full Name'
                            minLength={2}
                            value={requestForm.name}
                            onChange={(event) =>
                              setRequestForm((prev) => ({
                                ...prev,
                                name: event.target.value,
                              }))
                            }
                            required
                          />
                          <div className='invalid-feedback'>
                            Please enter your name.
                          </div>
                          <input
                            type='tel'
                            className='form-control'
                            placeholder='Phone Number'
                            aria-label='Phone Number'
                            pattern='[0-9]{10}'
                            minLength={10}
                            maxLength={10}
                            value={requestForm.phone}
                            onChange={(event) =>
                              setRequestForm((prev) => ({
                                ...prev,
                                phone: event.target.value,
                              }))
                            }
                            required
                          />
                          <div className='invalid-feedback'>
                            Enter a valid 10-digit phone number.
                          </div>
                          <textarea
                            className='form-control'
                            rows={3}
                            placeholder='Tell us your requirement'
                            aria-label='Requirement'
                            minLength={10}
                            value={requestForm.message}
                            onChange={(event) =>
                              setRequestForm((prev) => ({
                                ...prev,
                                message: event.target.value,
                              }))
                            }
                            required
                          ></textarea>
                          <div className='invalid-feedback'>
                            Please describe your requirement in at least 10
                            characters.
                          </div>
                          <button
                            type='submit'
                            className='btn brand-btn-primary w-100'
                            disabled={isLoading}
                          >
                            {isLoading ? "Submitting..." : "Submit Request"}
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          className='carousel-control-prev'
          type='button'
          data-bs-target='#heroCarousel'
          data-bs-slide='prev'
        >
          <span className='carousel-control-prev-icon'></span>
        </button>
        <button
          className='carousel-control-next'
          type='button'
          data-bs-target='#heroCarousel'
          data-bs-slide='next'
        >
          <span className='carousel-control-next-icon'></span>
        </button>
      </section>
      <ToastContainer position='bottom-end' className='p-3'>
        <Toast
          show={showSuccessToast}
          onClose={() => setShowSuccessToast(false)}
          delay={3000}
          autohide
          bg='success'
        >
          <Toast.Header closeButton>
            <strong className='me-auto'>Request Sent</strong>
          </Toast.Header>
          <Toast.Body className='text-white'>
            Success! Our team will contact you shortly.
          </Toast.Body>
        </Toast>
        <Toast
          show={showErrorToast}
          onClose={() => setShowErrorToast(false)}
          delay={4000}
          autohide
          bg='danger'
        >
          <Toast.Header closeButton>
            <strong className='me-auto'>Request Failed</strong>
          </Toast.Header>
          <Toast.Body className='text-white'>
            {errorMessage ||
              "Unable to submit request right now. Please try again."}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </section>
  );
};

export default HeroSection;
